import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Configuração de uma abertura da modal genérica de upload de foto.
 *
 * A mesma modal (cropper redondo + zoom) é reutilizada por usuário,
 * cliente e bandeira. Cada tela decide o que fazer com o arquivo
 * recortado através de `onConfirm`.
 */
export interface PhotoUploadConfig {
  /** Título exibido no cabeçalho. Padrão: "Alterar foto". */
  title?: string;
  /** Rótulo do botão de confirmação. Padrão: "Salvar foto". */
  confirmLabel?: string;
  /**
   * Mensagem de sucesso (toast) após `onConfirm` concluir.
   * Use `null` para não exibir toast (ex.: fluxo de criação, em que
   * o feedback é dado pela tela que criou a entidade).
   */
  successMessage?: string | null;
  /**
   * Ação executada com o arquivo recortado. Deve retornar um Observable.
   * - Upload imediato: `(file) => data.uploadCustomerImage(id, file)`
   * - Fluxo de criação (sem id ainda): guarde o arquivo e retorne `of(file)`.
   * Um valor `null` emitido é tratado como falha (sem fechar/sucesso).
   */
  onConfirm: (file: File) => Observable<any>;
  /** Callback chamado após sucesso, com a resposta do `onConfirm`. */
  onSuccess?: (response: any) => void;
}

/**
 * Controla a abertura da modal genérica de upload de foto.
 * Singleton: existe uma única instância de `<app-photo-upload-modal>`
 * montada no app-root, e qualquer tela pode abri-la via `open(config)`.
 */
@Injectable({ providedIn: 'root' })
export class PhotoUploadModalService {
  private openSubject$ = new Subject<PhotoUploadConfig>();
  open$ = this.openSubject$.asObservable();

  open(config: PhotoUploadConfig): void {
    this.openSubject$.next(config);
  }
}
